import { IAnnouncementBase, ICreateAnnouncement, IUpdateAnnouncement } from '@announcement/announcement.models';
import { apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import { envConfigs } from '@config/envConfigs';
import { convertRequestToFormData } from '@file/file.services';
import { IUploadFileRequest } from '@file/file.models';
import { IItemResponse } from '@gcl/react-core';
import { IProjectTeamBase } from '@projectTeam/projectTeam.models';
import {
  ISignature,
  ISyncNoAccountUserProfileByProjectNoRequest,
  ISyncNoAccountUserProfileByProjectNoResponse,
  IUpdateMySignatureRequest,
  IUpdateDummyUserProfileRequest,
  ICreateSignatureRequest,
} from '@userProfile/userProfile.models';
import { ICreateTrainingRequest, ITraining, IUpdateTrainingRequest } from '@userProfile/training.models';
import {
  IProjectTransmittalItemRevision,
  IProjectTransmittalItemRevisionRequest,
} from '@transmittal/transmittalItemRevision/transmittalItemRevision.models';
import {
  IProjectUserProfile,
  IProjectUserProfileByCompany,
  IUpdateProjectUserProfileRequest,
} from '@userProfile/projectUserProfile.models';
import { ICreateRoleRequest } from '@role/role.models';
import { ICreateProjectRoleRequest } from '@role/projectRole/projectRole.models';
import { ICreatePermissionRequest, ICreateRolePermissionRequest } from '@userProfile/permission/permission.models';
import { IFetchProjectUserProfileByCompanyRequest } from './admin.queries';

const apiRoutes = {
  getProjectTeams: (id: number) => `/Admins/ProjectTeams/${id}`,
  updateProjectTeams: '/Admins/ProjectTeams/Update',
  announcementsFileUpload: '/Admins/Announcements/File/Upload',
  announcement: '/Admins/Announcements',
  announcementWithId: (id: number) => `/Admins/Announcements/${id}`,
  userProfilesSyncNoAccountUsersByProjectNo: (projectNo: string) =>
    `/Admins/UserProfiles/SyncNoAccountUsers/ByProjectNo/${projectNo}`,
  projectUserProfilesWithId: (id: number) => `/Admins/ProjectUserProfiles/${id}`,
  training: '/Admins/Trainings',
  trainingWithId: (id: number) => `/Admins/Trainings/${id}`,
  trainingDailySync: '/Trainings/DailySync',
  getICECert: (id: number) => `/Admins/Transmittal/ICECert/${id}`,
  updateICECert: (id: number) => `/Admins/Transmittal/ICECert/${id}`,
  getProjectUserProfileByCompany: (id: number) => `/Admins/UserProfiles/${id}/ProjectUserProfile`,
  signatureWithSignature: (id: number) => `/Admins/Signatures/${id}`,
  signature: `/Admins/Signatures`,
  userProfilesReplaceDummyAccounts: '/Admins/UserProfiles/ReplaceDummyAccounts',
  roles: '/Admins/Roles',
  projectRoles: '/Admins/ProjectRoles',
  permissions: '/Admins/Permissions',
  rolePermissions: '/Admins/RolePermissions',
};

export const getAdminProjectTeamsAsync = async (id: number): Promise<IProjectTeamBase> => {
  const result = await apiServerGet<IProjectTeamBase>(`${envConfigs.apiBaseUrl}${apiRoutes.getProjectTeams(id)}`);
  return result.data;
};

export const updateAdminProjectTeamsAsync = async (props: IProjectTeamBase): Promise<IProjectTeamBase> => {
  const result = await apiServerPut<IProjectTeamBase>(`${envConfigs.apiBaseUrl}${apiRoutes.updateProjectTeams}`, props);
  return result.data;
};

export const postAnnouncementFileUploadAsync = async (
  uploadRequest: IUploadFileRequest,
): Promise<IItemResponse<string>> => {
  const formData = convertRequestToFormData(uploadRequest);
  const result = await apiServerPost<IItemResponse<string>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.announcementsFileUpload}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return result.data;
};

export const getAdminsAnnouncementAsync = async (id: number): Promise<IItemResponse<IAnnouncementBase>> => {
  const result = await apiServerGet<IItemResponse<IAnnouncementBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.announcementWithId(id)}`,
  );
  return result.data;
};

export const postAdminsAnnouncementAsync = async (
  props: ICreateAnnouncement,
): Promise<IItemResponse<IAnnouncementBase>> => {
  const result = await apiServerPost<IItemResponse<IAnnouncementBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.announcement}`,
    props,
  );
  return result.data;
};

export const putAdminsAnnouncementAsync = async (
  props: IUpdateAnnouncement,
): Promise<IItemResponse<IAnnouncementBase>> => {
  const result = await apiServerPut<IItemResponse<IAnnouncementBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.announcementWithId(props.id)}`,
    props,
  );
  return result.data;
};

export const putAdminsUserProfilesSyncNoAccountUsersByProjectNo = async (
  props: ISyncNoAccountUserProfileByProjectNoRequest,
): Promise<ISyncNoAccountUserProfileByProjectNoResponse> => {
  const result = await apiServerPut<ISyncNoAccountUserProfileByProjectNoResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.userProfilesSyncNoAccountUsersByProjectNo(props.projectNo)}`,
    props,
  );
  return result.data;
};

export const getAdminsTrainingAsync = async (id: number): Promise<IItemResponse<ITraining>> => {
  const result = await apiServerGet<IItemResponse<ITraining>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.trainingWithId(id)}`,
  );
  return result.data;
};

export const createAdminsTrainingAsync = async (request: ICreateTrainingRequest): Promise<IItemResponse<ITraining>> => {
  const result = await apiServerPost<IItemResponse<ITraining>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.training}`,
    request,
  );
  return result.data;
};
export const updateAdminsTrainingAsync = async (request: IUpdateTrainingRequest): Promise<IItemResponse<ITraining>> => {
  const result = await apiServerPut<IItemResponse<ITraining>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.trainingWithId(request.id)}`,
    request,
  );
  return result.data;
};
export const syncAdminsTrainingDailyAsync = async (): Promise<boolean> => {
  const result = await apiServerGet(`${envConfigs.apiBaseUrl}${apiRoutes.trainingDailySync}`);
  return result.data;
};
export const getICECertAsync = async (id: number): Promise<IProjectTransmittalItemRevision> => {
  const result = await apiServerGet<IProjectTransmittalItemRevision>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getICECert(id)}`,
  );
  return result.data;
};

export const updateICECertAsync = async (
  props: IProjectTransmittalItemRevisionRequest,
): Promise<IItemResponse<IProjectTransmittalItemRevision>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalItemRevision>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.updateICECert(props.id)}`,
  );
  return result.data;
};

export const getAdminsProjectUserProfileByCompany = async (
  id: number,
  props: IFetchProjectUserProfileByCompanyRequest | undefined,
) => {
  const result = await apiServerGet<IItemResponse<IProjectUserProfileByCompany>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getProjectUserProfileByCompany(id)}`,
    { params: props },
  );
  return result.data;
};

export const getAdminsSignatureAsync = async (id: number) => {
  const result = await apiServerGet<IItemResponse<ISignature>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.signatureWithSignature(id)}`,
  );
  return result.data;
};
export const postAdminsSignatureAsync = async (props: ICreateSignatureRequest) => {
  const formData = convertRequestToFormData(props);

  const result = await apiServerPost<IItemResponse<ISignature>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.signature}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return result.data;
};
export const updateAdminsSignatureAsync = async (
  props: IUpdateMySignatureRequest,
): Promise<IItemResponse<ISignature>> => {
  const formData = convertRequestToFormData(props);

  const result = await apiServerPut<IItemResponse<ISignature>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.signatureWithSignature(props.id)}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return result.data;
};

export const putAdminsProjectUserProfile = async (
  updateRequest: IUpdateProjectUserProfileRequest,
): Promise<IItemResponse<IProjectUserProfile>> => {
  const result = await apiServerPut<IItemResponse<IProjectUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.projectUserProfilesWithId(updateRequest.userProfile.id)}`,
    updateRequest,
  );
  return result.data;
};

export const putUserProfilesReplaceDummyAccounts = async (props: IUpdateDummyUserProfileRequest) => {
  const result = await apiServerPut<boolean>(
    `${envConfigs.apiBaseUrl}${apiRoutes.userProfilesReplaceDummyAccounts}`,
    props,
  );
  return result.data;
};

export const postAdminsRolesAsync = async (props: ICreateRoleRequest) => {
  const result = await apiServerPost(`${envConfigs.apiBaseUrl}${apiRoutes.roles}`, props);
  return result.data;
};

export const postAdminsProjectRolesAsync = async (props: ICreateProjectRoleRequest) => {
  const result = await apiServerPost(`${envConfigs.apiBaseUrl}${apiRoutes.projectRoles}`, props);
  return result.data;
};

export const postAdminsPermissionsAsync = async (props: ICreatePermissionRequest) => {
  const result = await apiServerPost(`${envConfigs.apiBaseUrl}${apiRoutes.permissions}`, props);
  return result.data;
};

export const postAdminsRolePermissionsAsync = async (props: ICreateRolePermissionRequest) => {
  const result = await apiServerPost(`${envConfigs.apiBaseUrl}${apiRoutes.rolePermissions}`, props);
  return result.data;
};
